import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { BonusIcon } from './BonusRow';
import { PRESET_LIBRARY, fmtUSD, WEEKLY_BLOCKED, WEEKLY_BLOCK_NOTE, type PresetBonus, type PresetTag } from './types';

const FILTER_TAGS: { id: 'all' | PresetTag; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'classic', label: 'Classic' },
  { id: 'inverted', label: 'Inverted' },
  { id: 'sabotage', label: 'Sabotage' },
];

export function PresetPickerDialog({
  open,
  onOpenChange,
  onPick,
  existingPresetKeys,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (preset: PresetBonus) => void;
  existingPresetKeys: string[];
}) {
  const [filter, setFilter] = useState<'all' | PresetTag>('all');
  const filtered = PRESET_LIBRARY.filter(
    (p) => filter === 'all' || p.tag === filter,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Preset Bonus</DialogTitle>
        </DialogHeader>

        {/* Category filter */}
        <div className="flex gap-1.5 flex-wrap">
          {FILTER_TAGS.map((t) => (
            <Button
              key={t.id}
              size="sm"
              variant={filter === t.id ? 'default' : 'outline'}
              className="h-8 text-xs"
              onClick={() => setFilter(t.id)}
            >
              {t.label}
            </Button>
          ))}
        </div>

        {/* Preset grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-[60vh] overflow-y-auto pr-1">
          {filtered.map((p) => {
            const used = existingPresetKeys.includes(p.preset_key);
            // SAB-632 STEP 0: weekly presets are blocked until SAB-648 ships their payout.
            const blocked = WEEKLY_BLOCKED && p.frequency === 'per_week';
            return (
              <button
                key={p.preset_key}
                disabled={used || blocked}
                onClick={() => { if (!blocked) onPick(p); }}
                title={blocked ? WEEKLY_BLOCK_NOTE : undefined}
                className={cn(
                  'text-left rounded-xl p-3.5 border transition-all flex flex-col gap-2',
                  used || blocked
                    ? 'bg-muted border-border/50 opacity-50 cursor-not-allowed'
                    : 'bg-card border-border hover:border-orange-500 cursor-pointer',
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-orange-500/12 text-orange-500 flex items-center justify-center shrink-0">
                      <BonusIcon name={p.icon} className="w-4 h-4" />
                    </div>
                    <div className="font-bold text-[13px] truncate">{p.name}</div>
                  </div>
                  {blocked ? (
                    <span className="text-[10px] text-muted-foreground/70 uppercase tracking-wide shrink-0">
                      Unavailable
                    </span>
                  ) : used ? (
                    <span className="text-[10px] text-muted-foreground/70 uppercase tracking-wide shrink-0">
                      Added
                    </span>
                  ) : (
                    <span className="text-[11px] font-bold text-orange-500 shrink-0">
                      {fmtUSD(p.suggested)}
                      {p.frequency === 'per_week' ? '/wk' : ''}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-muted-foreground leading-snug">
                  {blocked ? WEEKLY_BLOCK_NOTE : p.description}
                </div>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
